/**
 * Zero-Based model — every dollar gets a job. Categories carry a
 * monthly allocation; the pool tracks what's still unassigned.
 */

import type { AppData } from '../../data/types';
import type { PayPeriod } from '../payPeriod';
import {
  AVG_DAYS_PER_MONTH,
  computeRolloverAmount,
  resolvePeriodIncome,
  roundMoney,
  sumExpensesInRange,
  sumExpensesInRangeWhere,
  sumIncomeInRange,
  sumSpendingWithScheduledInRange,
} from './shared';
import { _classifyDailyStatus, type DailyStatus } from './daily';

export interface CategoryAllocation {
  categoryId: string;
  name: string;
  color: string;
  /** Period allocation derived from monthly zeroBasedBudget. */
  allocated: number;
  spent: number;
  remaining: number;
  progress: number;
}

export interface ZeroBasedResult {
  period: PayPeriod;
  // Running-balance fields (fallback when no accounts are linked)
  availableToday: number;
  dailyIncomeRate: number;
  remainingThisPeriod: number;
  dayInPeriod: number;
  totalDays: number;
  daysRemaining: number;
  spentToday: number;
  spentThisPeriod: number;
  status: DailyStatus;
  statusLabel: string;
  periodIncome: number;
  rolloverAmount: number;
  // Zero-based specific
  totalAllocatedMonthly: number;
  toAssignMonthly: number;
  toAssignPeriod: number;
  perCategory: CategoryAllocation[];
}

export function calculateZero(
  data: AppData,
  period: PayPeriod,
  today: Date,
): ZeroBasedResult {
  const periodIncome = resolvePeriodIncome(data, period);
  const rolloverAmount = computeRolloverAmount(data, period);

  const incomeToDate = sumIncomeInRange(data.transactions, period.start, today);
  // Past scheduled bills auto-deduct on their due date — included
  // alongside transaction-based spending in the running balance.
  const spendingToDate = sumSpendingWithScheduledInRange(
    data,
    period.start,
    today,
  );
  const availableToday = roundMoney(
    rolloverAmount + incomeToDate - spendingToDate,
  );
  const dailyIncomeRate =
    period.totalDays > 0 ? roundMoney(periodIncome / period.totalDays) : 0;
  const remainingThisPeriod = roundMoney(periodIncome - spendingToDate);
  const spentToday = sumExpensesInRange(data.transactions, today, today);
  const spentThisPeriod = spendingToDate;
  const { status, statusLabel } = _classifyDailyStatus(
    availableToday,
    dailyIncomeRate,
  );

  const periodScale = period.totalDays / AVG_DAYS_PER_MONTH;
  const expenseCats = data.categories.filter((c) => c.type === 'expense');
  const perCategory: CategoryAllocation[] = expenseCats.map((c) => {
    const allocatedMonthly = c.zeroBasedBudget ?? 0;
    const allocated = roundMoney(allocatedMonthly * periodScale);
    const spent = sumExpensesInRangeWhere(
      data.transactions,
      period.start,
      period.end,
      (t) => t.categoryId === c.id,
    );
    const remaining = roundMoney(allocated - spent);
    const progress =
      allocated > 0 ? Math.min(1, Math.max(0, spent / allocated)) : 0;
    return {
      categoryId: c.id,
      name: c.name,
      color: c.color,
      allocated,
      spent,
      remaining,
      progress,
    };
  });
  const totalAllocatedMonthly = roundMoney(
    expenseCats.reduce((a, c) => a + (c.zeroBasedBudget ?? 0), 0),
  );
  const monthlyAnchor =
    periodScale > 0 ? roundMoney(periodIncome / periodScale) : 0;
  const toAssignMonthly = roundMoney(monthlyAnchor - totalAllocatedMonthly);
  const toAssignPeriod = roundMoney(toAssignMonthly * periodScale);

  return {
    period,
    availableToday,
    dailyIncomeRate,
    remainingThisPeriod,
    dayInPeriod: period.dayInPeriod,
    totalDays: period.totalDays,
    daysRemaining: period.daysRemaining,
    spentToday,
    spentThisPeriod,
    status,
    statusLabel,
    periodIncome,
    rolloverAmount,
    totalAllocatedMonthly,
    toAssignMonthly,
    toAssignPeriod,
    perCategory,
  };
}

export { calculateZero as calculateZeroBased };
